'use client';

import { type ReactNode, type ReactElement, useContext, useEffect } from 'react';
import { StoreContext } from './store.provider';
import { useRouter, useSearchParams } from 'next/navigation';
import { observer } from 'mobx-react-lite';
import { routes } from '@/lib/constants/routes';
import { useAccount, useRefresh } from '@/lib/services/auth/service';
import { useNormalizePathname } from '@/lib/hooks/use-pathname';

export default observer(function AuthProvider({
  children,
}: Readonly<{ readonly children: ReactNode }>): ReactElement {
  const pathname = useNormalizePathname();
  const searchParams = useSearchParams();
  const { userStore } = useContext(StoreContext);
  const { account } = useAccount();
  const { refresh } = useRefresh();
  const router = useRouter();

  const redirect = (isAuth: boolean) => {
    const onAuth = searchParams.get('on-auth');
    if (isAuth && onAuth) return router.push(Buffer.from(onAuth, 'base64').toString('utf-8'));
    if (isAuth && routes.requiresNoAuth(pathname)) return router.push('/account');
    else if (!isAuth && routes.requiresAuth(pathname)) {
      const query = new URLSearchParams(searchParams.toString());
      return router.push(
        `/auth?on-auth=${Buffer.from(`${pathname}?${query.toString()}`).toString('base64')}`,
      );
    }
  };

  useEffect(() => {
    (async () => {
      if (userStore.auth) return redirect(userStore.auth);
      if (localStorage.getItem('access-token') && !userStore.auth) {
        try {
          const auth = await refresh.mutateAsync();
          return redirect(auth.ok);
        } catch {
          localStorage.removeItem('access-token');
          return redirect(false);
        }
      }
      return redirect(false);
    })();
  }, [userStore.auth, pathname]);

  useEffect(() => {
    (async () => {
      if (userStore.auth) await account.refetch();
    })();
  }, [userStore.auth]);

  return <>{children}</>;
});
